import { IconButton, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Link as RouterLink, useNavigate } from "react-router";
import { ArrowForward } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSignedInUser } from "@platform/user/client";
import { Link, Typography } from "@platform/design-system";

import { ROUTE_NAMES } from "../../constants";
import { RFP_STATUSES } from "../../constants/rfp";
import { formatDate } from "../../utils";
import { NonAdminDiscardCellParams } from "../../types/rfpGridContext";

import type { ICellRendererParams } from "ag-grid-community";

export const CreatorCellRender: React.FC<ICellRendererParams> = ({
  value,
  data,
  colDef,
}) => {
  const field = colDef.field;

  return (
    <Stack
      spacing={0.5}
      sx={{
        width: "100%",
        whiteSpace: "normal",
        wordBreak: "break-all",
        overflowWrap: "normal",
        padding: "0.5rem 0",
      }}
    >
      <Typography variant="body-medium">{value}</Typography>
      <Typography variant="caption">{data?.[field]?.mail}</Typography>
    </Stack>
  );
};

export const AnalystCellRender: React.FC<ICellRendererParams> = ({
  value,
  data,
}) => (
  <Stack
    spacing={0}
    style={{
      width: "100%",
      whiteSpace: "normal",
      wordBreak: "break-all",
      overflowWrap: "normal",
      fontStyle: value === "Pending" ? "italic" : "normal",
    }}
  >
    <Typography variant="body-medium">{value}</Typography>
    <Typography variant="body-medium">{data.assignedAnalyst?.email}</Typography>
  </Stack>
);

export const ClientInfoCell: React.FC<ICellRendererParams> = ({
  value,
  data,
}) => (
  <Stack
    sx={{ wordBreak: "break-word", whiteSpace: "normal", padding: "0.5rem 0" }}
  >
    <Typography variant="body-large">
      <strong>{value}</strong>
    </Typography>
    <Typography variant="body-medium">
      RFP with{" "}
      <Typography
        variant="body-medium"
        component="span"
        className={data.products?.length ? "secondary-color" : null}
      >
        {data.products?.length} products
      </Typography>
    </Typography>
  </Stack>
);

export const ExpandIconCell: React.FC<ICellRendererParams> = ({
  value,
  node,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const onClick = useCallback(() => {
    setOpen((prev) => !prev);
    node.setExpanded(!node.expanded);
  }, [setOpen, node]);

  return (
    <Stack
      spacing={0.5}
      direction="row"
      display="flex"
      alignItems="center"
      sx={{
        minWidth: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <IconButton
        aria-label="expand row"
        size="small"
        onClick={onClick}
        sx={{ flexShrink: 0 }}
      >
        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>

      <Typography variant="body-medium" sx={{ minWidth: 0 }}>
        {value}
      </Typography>
    </Stack>
  );
};

export const StatusCell: React.FC<ICellRendererParams> = ({ value, data }) => (
  <Stack
    direction="row"
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      whiteSpace: "nowrap",
      flexWrap: "nowrap",
      width: "100%",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        whiteSpace: "normal",
      }}
      variant="body-medium"
      flex={1}
    >
      {RFP_STATUSES[value] ?? "TBD"}
    </Typography>

    <Link
      component={RouterLink}
      to={`/${ROUTE_NAMES.RFPS}/${data.id}`}
      underline="hover"
      sx={{ display: "flex", alignItems: "center" }}
    >
      <ArrowForward />
    </Link>
  </Stack>
);

export const NonAdminDiscardCell: React.FC<NonAdminDiscardCellParams> = ({
  value,
  data,
  openDiscardDialog,
}) => {
  const navigate = useNavigate();
  const { isAdminOrConsultant: adminOrConsultantUser } = useSignedInUser();
  const formattedDate = (value ? formatDate(value) : "-") as string;

  const onClick = useCallback(() => {
    openDiscardDialog(data);
  }, [data, openDiscardDialog]);

  return !adminOrConsultantUser ? (
    <Stack
      data-testid="non-admin-cell"
      direction="row"
      justifyContent="space-between"
      width="100%"
    >
      {formattedDate}

      <Stack direction="row" display="flex" alignItems="center">
        <Link
          component="button"
          underline="hover"
          onClick={() =>
            navigate(`/${ROUTE_NAMES.RFP}`, {
              state: { prevPath: location.pathname, draftRfpId: data.id },
            })
          }
          sx={{ display: "flex", alignItems: "center" }}
        >
          <ArrowForwardIcon /> CONTINUE
        </Link>

        <Link
          component="button"
          underline="hover"
          onClick={onClick}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <DeleteOutlineIcon /> DISCARD
        </Link>
      </Stack>
    </Stack>
  ) : (
    <>{formattedDate}</>
  );
};
